import { useEditor, EditorContent, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import ResizableImageNode from "./ResizableImageNode";

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: "center",
        parseHTML: el => el.getAttribute("data-align") || "center",
        renderHTML: attrs => attrs.align ? { "data-align": attrs.align } : {},
      },
      widthPct: {
        default: null,
        parseHTML: el => {
          const s = el.getAttribute("style") || "";
          const m = s.match(/width:\s*([0-9.]+)\s*%/);
          return m ? parseFloat(m[1]) : null;
        },
        renderHTML: attrs => {
          const w = attrs.widthPct ? `width: ${attrs.widthPct}%;` : "";
          if (attrs.align === "wrap-left")  return { style: `${w} float: left;  margin: 0.25rem 1.25rem 0.75rem 0; height: auto;`.trim() };
          if (attrs.align === "wrap-right") return { style: `${w} float: right; margin: 0.25rem 0 0.75rem 1.25rem; height: auto;`.trim() };
          if (attrs.align === "full")       return { style: "width: 100%; display: block; margin: 1rem 0; height: auto;" };
          let align = "margin: 1rem auto; display: block;";
          if (attrs.align === "left")   align = "margin: 1rem auto 1rem 0; display: block;";
          if (attrs.align === "right")  align = "margin: 1rem 0 1rem auto; display: block;";
          return { style: `${w} ${align} height: auto;`.trim() };
        },
      },
      // Legacy `style` attribute retained for backward compatibility with previously
      // saved images that used inline style widths.
      style: {
        default: null,
        parseHTML: el => el.getAttribute("style"),
        renderHTML: () => ({}), // rendered via widthPct/align now
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNode);
  },
});
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { useCallback, useEffect, useRef } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Highlighter,
  List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo2, Redo2, Eraser,
  Table as TableIcon, Rows3, Columns3, Trash2, Plus, Minus, Heading1, Heading2, Heading3, Maximize2, WrapText
} from "lucide-react";
import { api } from "@/lib/api";

/* ---------- Toolbar button ---------- */
const Btn = ({ onClick, active, disabled, title, children, testId }) => (
  <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} disabled={disabled}
    title={title} data-testid={testId}
    className={`px-2 py-1.5 rounded-md text-sm border transition-colors
      ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}
      disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1`}>
    {children}
  </button>
);
const Sep = () => <div className="w-px h-6 bg-slate-200 mx-0.5"/>;

const FONTS = [
  { l: "Default",  v: "" },
  { l: "Serif",    v: "Georgia, 'Playfair Display', serif" },
  { l: "Sans",     v: "Inter, system-ui, sans-serif" },
  { l: "Mono",     v: "'JetBrains Mono', monospace" },
  { l: "Playfair", v: "'Playfair Display', serif" },
  { l: "Arial",    v: "Arial, sans-serif" },
  { l: "Times",    v: "'Times New Roman', serif" },
];
const SIZES = ["12px","14px","16px","18px","20px","24px","28px","32px","40px","56px"];
const COLORS = ["#000000","#374151","#6B7280","#D22B2B","#F97316","#F59E0B","#10B981","#0EA5E9","#4F46E5","#8B5CF6","#EC4899"];

/**
 * Full-featured rich-text editor (Tiptap based) for Admin Pages.
 * Emits HTML on change. `value` and `onChange(html)` mirror the simpler editor.
 */
export default function RichTextEditorPro({ value = "", onChange, minHeight = "360px", testId = "rte-pro" }) {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
      Underline,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      ResizableImage.configure({ inline: false, allowBase64: true, HTMLAttributes: { class: "rte-img" } }),
      Table.configure({ resizable: true, HTMLAttributes: { class: "rte-table" } }),
      TableRow, TableHeader, TableCell,
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => { onChange && onChange(editor.getHTML()); },
    editorProps: {
      attributes: { class: "prose-editorial rte-editor focus:outline-none", "data-testid": `${testId}-editor` },
    },
  });

  // Keep editor content in sync when `value` prop changes externally
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) editor.commands.setContent(value, false);
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const uploadImage = useCallback(async (file) => {
    if (!editor || !file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = res.data?.url || res.data?.path || res.data?.location;
      if (url) editor.chain().focus().setImage({ src: url }).run();
    } catch { /* fall back to base64 */
      const reader = new FileReader();
      reader.onload = e => editor.chain().focus().setImage({ src: e.target.result }).run();
      reader.readAsDataURL(file);
    }
  }, [editor]);

  const insertImageUrl = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const setImageWidthPct = (val) => {
    if (!editor) return;
    editor.chain().focus().updateAttributes("image", { widthPct: val }).run();
  };
  const setImageAlign = (align) => {
    if (!editor) return;
    editor.chain().focus().updateAttributes("image", { align }).run();
  };

  const selectedImage = (() => {
    if (!editor) return null;
    const node = editor.state.doc.nodeAt(editor.state.selection.from);
    return node && node.type.name === "image" ? node : null;
  })();
  const currentImageWidthPct = selectedImage?.attrs?.widthPct || null;
  const currentImageAlign = selectedImage?.attrs?.align || "center";

  const insertTable = () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();

  if (!editor) return null;
  const inTable = editor.isActive("table");
  const imageSelected = !!selectedImage;

  return (
    <div className="rte-pro-wrap border border-slate-200 rounded-2xl overflow-hidden bg-white" data-testid={testId}>
      <style>{`
        .rte-pro-wrap .ProseMirror { min-height: ${minHeight}; padding: 18px 20px; color: #0F172A; font-size: 15px; line-height: 1.7; }
        .rte-pro-wrap .ProseMirror:focus { outline: none; }
        .rte-pro-wrap .ProseMirror p { margin: 0 0 0.8rem; }
        .rte-pro-wrap .ProseMirror h1 { font-family: 'Playfair Display', serif; font-size: 2rem; margin: 1.2rem 0 0.6rem; font-weight: 800; }
        .rte-pro-wrap .ProseMirror h2 { font-family: 'Playfair Display', serif; font-size: 1.6rem; margin: 1rem 0 0.5rem; font-weight: 700; }
        .rte-pro-wrap .ProseMirror h3 { font-family: 'Playfair Display', serif; font-size: 1.3rem; margin: 0.9rem 0 0.45rem; font-weight: 700; }
        .rte-pro-wrap .ProseMirror ul, .rte-pro-wrap .ProseMirror ol { padding-left: 1.5rem; margin: 0 0 0.9rem; }
        .rte-pro-wrap .ProseMirror ul { list-style: disc; } .rte-pro-wrap .ProseMirror ol { list-style: decimal; }
        .rte-pro-wrap .ProseMirror blockquote { border-left: 4px solid #BAE6FD; padding: 0.5rem 1rem; background: #F0F9FF; border-radius: 0 12px 12px 0; margin: 1rem 0; color: #334155; font-style: italic; }
        .rte-pro-wrap .ProseMirror pre { background: #0F172A; color: #E2E8F0; padding: 12px 16px; border-radius: 10px; font-family: 'JetBrains Mono', monospace; font-size: 0.9rem; overflow-x: auto; }
        .rte-pro-wrap .ProseMirror code { background: #F1F5F9; color: #0F172A; padding: 2px 6px; border-radius: 4px; font-family: 'JetBrains Mono', monospace; font-size: 0.9em; }
        .rte-pro-wrap .ProseMirror pre code { background: transparent; color: inherit; padding: 0; }
        .rte-pro-wrap .ProseMirror a { color: #0369A1; text-decoration: underline; }
        .rte-pro-wrap .ProseMirror hr { border: 0; border-top: 1px solid #E5E7EB; margin: 1.5rem 0; }
        .rte-pro-wrap .ProseMirror .rte-img { max-width: 100%; height: auto; display: block; border-radius: 12px; margin: 1rem auto; }
        .rte-pro-wrap .ProseMirror .rte-img.ProseMirror-selectednode { outline: 2px solid #0EA5E9; outline-offset: 4px; }

        /* Resizable image node view */
        .rte-pro-wrap .ProseMirror .rte-img-wrap { position: relative; display: block; line-height: 0; margin: 0; }
        .rte-pro-wrap .ProseMirror .rte-img-wrap .rte-img { width: 100%; display: block; margin: 0; }
        /* Clearfix: keep floats contained inside the editor so they don't spill past the ProseMirror block. */
        .rte-pro-wrap .ProseMirror::after { content: ""; display: block; clear: both; }
        .rte-pro-wrap .ProseMirror .rte-img-wrap.is-selected .rte-img { outline: 2px solid #0EA5E9; outline-offset: 2px; }
        /* Tiptap's outer wrapper for the image node — style hook. */
        .rte-pro-wrap .ProseMirror > .react-renderer.node-image { margin: 1rem auto; }

        .rte-pro-wrap .ProseMirror .rte-img-handle {
          position: absolute; width: 14px; height: 14px; background: #0EA5E9; border: 2px solid #FFFFFF;
          border-radius: 50%; box-shadow: 0 1px 4px rgba(15,23,42,0.35); z-index: 5;
        }
        .rte-pro-wrap .ProseMirror .rte-img-handle-tl { top: -8px;    left: -8px;    cursor: nwse-resize; }
        .rte-pro-wrap .ProseMirror .rte-img-handle-tr { top: -8px;    right: -8px;   cursor: nesw-resize; }
        .rte-pro-wrap .ProseMirror .rte-img-handle-bl { bottom: -8px; left: -8px;    cursor: nesw-resize; }
        .rte-pro-wrap .ProseMirror .rte-img-handle-br { bottom: -8px; right: -8px;   cursor: nwse-resize; }

        .rte-pro-wrap .ProseMirror .rte-img-align-bar {
          position: absolute; top: -34px; left: 50%; transform: translateX(-50%);
          background: #0F172A; border-radius: 8px; padding: 3px; display: inline-flex; gap: 2px;
          box-shadow: 0 4px 12px rgba(15,23,42,0.3); z-index: 6;
        }
        .rte-pro-wrap .ProseMirror .rte-img-align-bar button {
          background: transparent; border: 0; color: #E2E8F0; padding: 4px 6px; border-radius: 5px; cursor: pointer;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .rte-pro-wrap .ProseMirror .rte-img-align-bar button:hover { background: rgba(255,255,255,0.12); }
        .rte-pro-wrap .ProseMirror .rte-img-align-bar button.active { background: #0EA5E9; color: white; }
        .rte-pro-wrap .ProseMirror .rte-img-align-sep { width: 1px; height: 16px; background: rgba(255,255,255,0.25); margin: 0 3px; align-self: center; }

        .rte-pro-wrap .ProseMirror .rte-img-size-badge {
          position: absolute; bottom: 6px; right: 6px; background: rgba(15,23,42,0.85); color: white;
          padding: 2px 8px; border-radius: 6px; font-size: 11px; font-weight: 600; z-index: 6; pointer-events: none;
        }
        .rte-pro-wrap .ProseMirror .rte-table { border-collapse: collapse; width: 100%; margin: 1rem 0; table-layout: fixed; overflow: hidden; }
        .rte-pro-wrap .ProseMirror .rte-table th, .rte-pro-wrap .ProseMirror .rte-table td { border: 1px solid #CBD5E1; padding: 8px 10px; vertical-align: top; position: relative; min-width: 60px; }
        .rte-pro-wrap .ProseMirror .rte-table th { background: #F1F5F9; font-weight: 700; text-align: left; }
        .rte-pro-wrap .ProseMirror .rte-table .selectedCell:after { background: rgba(14,165,233,0.15); content: ""; left: 0; right: 0; top: 0; bottom: 0; pointer-events: none; position: absolute; z-index: 2; }
        .rte-pro-wrap .ProseMirror .rte-table p { margin: 0; }
        .rte-pro-wrap .ProseMirror .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background-color: #0EA5E9; pointer-events: none; }
      `}</style>

      {/* ==================== TOOLBAR ==================== */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-200 bg-slate-50/70" data-testid={`${testId}-toolbar`}>
        <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="rte-undo"><Undo2 className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="rte-redo"><Redo2 className="w-4 h-4"/></Btn>
        <Sep/>

        {/* Font family */}
        <select className="h-8 px-2 border border-slate-200 rounded-md text-xs bg-white" title="Font"
          onChange={e => e.target.value ? editor.chain().focus().setFontFamily(e.target.value).run() : editor.chain().focus().unsetFontFamily().run()}
          value={editor.getAttributes("textStyle").fontFamily || ""}
          data-testid="rte-font-family">
          {FONTS.map(f => <option key={f.l} value={f.v}>{f.l}</option>)}
        </select>

        {/* Font size */}
        <select className="h-8 px-2 border border-slate-200 rounded-md text-xs bg-white" title="Size"
          onChange={e => e.target.value ? editor.chain().focus().setFontSize(e.target.value).run() : editor.chain().focus().unsetFontSize().run()}
          value={editor.getAttributes("textStyle").fontSize || ""}
          data-testid="rte-font-size">
          <option value="">Size</option>
          {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* Headings */}
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading",{level:1})} title="Heading 1" testId="rte-h1"><Heading1 className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading",{level:2})} title="Heading 2" testId="rte-h2"><Heading2 className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading",{level:3})} title="Heading 3" testId="rte-h3"><Heading3 className="w-4 h-4"/></Btn>
        <Sep/>

        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId="rte-bold"><Bold className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId="rte-italic"><Italic className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testId="rte-underline"><UnderlineIcon className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strike"><Strikethrough className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHighlight({ color: "#FEF08A" }).run()} active={editor.isActive("highlight")} title="Highlight"><Highlighter className="w-4 h-4"/></Btn>

        {/* Text color */}
        <div className="flex items-center gap-0.5" title="Text color">
          {COLORS.map(c => (
            <button key={c} type="button" onMouseDown={e => e.preventDefault()}
              onClick={() => editor.chain().focus().setColor(c).run()}
              className="w-4 h-4 rounded-full border border-slate-300 hover:scale-110 transition-transform"
              style={{ background: c }}
              title={c}
              data-testid={`rte-color-${c}`}/>
          ))}
        </div>
        <Sep/>

        <Btn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left"><AlignLeft className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center"><AlignCenter className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align right"><AlignRight className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify"><AlignJustify className="w-4 h-4"/></Btn>
        <Sep/>

        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><List className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote"><Quote className="w-4 h-4"/></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Code block"><Code className="w-4 h-4"/></Btn>
        <Sep/>

        <Btn onClick={setLink} active={editor.isActive("link")} title="Link" testId="rte-link"><LinkIcon className="w-4 h-4"/></Btn>
        <Btn onClick={() => fileRef.current?.click()} title="Upload image" testId="rte-upload-image"><ImageIcon className="w-4 h-4"/> Upload</Btn>
        <Btn onClick={insertImageUrl} title="Insert image by URL" testId="rte-image-url"><ImageIcon className="w-4 h-4"/> URL</Btn>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => { if (e.target.files?.[0]) { uploadImage(e.target.files[0]); e.target.value = ""; } }}/>
        <Sep/>

        <Btn onClick={insertTable} title="Insert 3x3 table" testId="rte-table-insert"><TableIcon className="w-4 h-4"/> Table</Btn>
        <Btn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testId="rte-clear"><Eraser className="w-4 h-4"/></Btn>
      </div>

      {/* Contextual second row: table controls (when in table) */}
      {inTable && (
        <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-sky-50/60" data-testid="rte-table-toolbar">
          <span className="text-[10px] uppercase tracking-wider text-sky-800 font-bold mr-1">Table</span>
          <Btn onClick={() => editor.chain().focus().addColumnBefore().run()} title="Add column before"><Columns3 className="w-4 h-4"/><Plus className="w-3 h-3"/> col ←</Btn>
          <Btn onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column after"><Columns3 className="w-4 h-4"/><Plus className="w-3 h-3"/> col →</Btn>
          <Btn onClick={() => editor.chain().focus().deleteColumn().run()} title="Delete column"><Columns3 className="w-4 h-4"/><Minus className="w-3 h-3"/></Btn>
          <Sep/>
          <Btn onClick={() => editor.chain().focus().addRowBefore().run()} title="Add row above"><Rows3 className="w-4 h-4"/><Plus className="w-3 h-3"/> row ↑</Btn>
          <Btn onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row below"><Rows3 className="w-4 h-4"/><Plus className="w-3 h-3"/> row ↓</Btn>
          <Btn onClick={() => editor.chain().focus().deleteRow().run()} title="Delete row"><Rows3 className="w-4 h-4"/><Minus className="w-3 h-3"/></Btn>
          <Sep/>
          <Btn onClick={() => editor.chain().focus().toggleHeaderRow().run()} title="Toggle header row">Header row</Btn>
          <Btn onClick={() => editor.chain().focus().mergeCells().run()} title="Merge selected">Merge</Btn>
          <Btn onClick={() => editor.chain().focus().splitCell().run()} title="Split cell">Split</Btn>
          <Sep/>
          <Btn onClick={() => editor.chain().focus().deleteTable().run()} title="Delete entire table"><Trash2 className="w-4 h-4"/> Delete table</Btn>
        </div>
      )}

      {/* Contextual second row: image controls (when image selected) */}
      {imageSelected && (
        <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-b border-slate-200 bg-emerald-50/60" data-testid="rte-image-toolbar">
          <span className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Image</span>
          {/* Quick preset percentages */}
          <Btn onClick={() => setImageWidthPct(25)}  active={currentImageWidthPct === 25}  title="25% width">25%</Btn>
          <Btn onClick={() => setImageWidthPct(50)}  active={currentImageWidthPct === 50}  title="50% width">50%</Btn>
          <Btn onClick={() => setImageWidthPct(75)}  active={currentImageWidthPct === 75}  title="75% width">75%</Btn>
          <Btn onClick={() => setImageWidthPct(100)} active={currentImageWidthPct === 100} title="100% width">100%</Btn>
          <Btn onClick={() => setImageWidthPct(null)} title="Reset to natural size">Auto</Btn>
          <Sep/>
          {/* Alignment */}
          <span className="text-[11px] text-slate-600">Align:</span>
          <Btn onClick={() => setImageAlign("left")}   active={currentImageAlign === "left"}   title="Align left" testId="rte-image-align-left"><AlignLeft className="w-4 h-4"/></Btn>
          <Btn onClick={() => setImageAlign("center")} active={currentImageAlign === "center"} title="Align center" testId="rte-image-align-center"><AlignCenter className="w-4 h-4"/></Btn>
          <Btn onClick={() => setImageAlign("right")}  active={currentImageAlign === "right"}  title="Align right" testId="rte-image-align-right"><AlignRight className="w-4 h-4"/></Btn>
          <Btn onClick={() => setImageAlign("full")}   active={currentImageAlign === "full"}   title="Full width" testId="rte-image-align-full"><Maximize2 className="w-4 h-4"/></Btn>
          <Sep/>
          <span className="text-[11px] text-slate-600">Wrap:</span>
          <Btn onClick={() => setImageAlign("wrap-left")}  active={currentImageAlign === "wrap-left"}  title="Wrap text on the right (image floats left)"  testId="rte-image-align-wrap-left"><WrapText className="w-4 h-4" style={{ transform: "scaleX(-1)" }}/></Btn>
          <Btn onClick={() => setImageAlign("wrap-right")} active={currentImageAlign === "wrap-right"} title="Wrap text on the left (image floats right)"  testId="rte-image-align-wrap-right"><WrapText className="w-4 h-4"/></Btn>
          <Sep/>
          <span className="text-[11px] text-slate-500 italic">Tip: drag the blue corners to resize.</span>
          <Sep/>
          <Btn onClick={() => editor.chain().focus().deleteSelection().run()} title="Remove image"><Trash2 className="w-4 h-4"/> Remove</Btn>
        </div>
      )}

      {/* ==================== EDITOR ==================== */}
      <EditorContent editor={editor}/>
    </div>
  );
}
